import supabase from "@/services/supabaseClient";
import { resolvePatientFacingDoctorName } from "@/utils/doctorPatientFacingName.js";
import { buildAlertRecord } from "@/utils/alertModel.js";

function isRecoverableReadDbError(error) {
  const code = String(error?.code || "").trim();
  const message = String(error?.message || "").toLowerCase();
  return (
    code === "42P01" ||
    code === "42501" ||
    message.includes("permission denied") ||
    message.includes("does not exist")
  );
}

function dispatchNotificationsChanged(detail = {}) {
  if (typeof window === "undefined") return;
  window.dispatchEvent(
    new CustomEvent("afrimed:notifications:changed", { detail })
  );
}

async function insertNotification({
  userId,
  title,
  message,
  type,
  priority = "routine",
  source,
  audience,
  channels,
  quietByDefault,
  escalationEligible,
  groupKey,
}) {
  const targetUserId = String(userId || "").trim();
  const notificationType = String(type || "appointment").trim() || "appointment";
  if (!targetUserId) return null;

  try {
    const payload = buildAlertRecord(
      {
        title,
        message,
        type: notificationType,
        priority,
        source,
        audience,
        channels,
        quiet_by_default: quietByDefault,
        escalation_eligible: escalationEligible,
        group_key: groupKey,
      },
      { fallbackAudience: audience || "mixed" }
    );
    const { data, error } = await supabase.rpc("afrimed_appointment_notification_upsert", {
      target_user: targetUserId,
      title_input: payload.title,
      message_input: payload.message,
      type_input: notificationType,
      priority_input: payload.priority,
      source_input: payload.source || null,
      audience_input: payload.audience || null,
      channels_input: payload.channels || null,
      quiet_by_default_input: payload.quiet_by_default ?? null,
      escalation_eligible_input: payload.escalation_eligible ?? null,
      group_key_input: payload.group_key || null,
    });
    if (error) throw error;
    dispatchNotificationsChanged({ userId: targetUserId, notificationId: data?.id || null });
    return { ...data, user_id: targetUserId };
  } catch (error) {
    throw error;
  }
}

async function resolvePatientAuthUserId({ appointmentId, patientUserId, patientRef }) {
  const directUserId = String(patientUserId || "").trim();
  const refs = Array.from(
    new Set(
      [patientRef, directUserId]
        .map((value) => String(value || "").trim())
        .filter(Boolean)
    )
  );

  if (appointmentId) {
    const { data: appointmentRow, error: appointmentError } = await supabase
      .from("appointments")
      .select("patient_id")
      .eq("id", String(appointmentId).trim())
      .maybeSingle();

    if (appointmentError && !isRecoverableReadDbError(appointmentError)) {
      throw appointmentError;
    }

    const appointmentPatientId = String(appointmentRow?.patient_id || "").trim();
    if (appointmentPatientId && !refs.includes(appointmentPatientId)) {
      refs.push(appointmentPatientId);
    }
  }

  if (refs.length === 0) return directUserId || null;

  const [{ data: byPatientId, error: byPatientIdError }, { data: byUserId, error: byUserIdError }] =
    await Promise.all([
      supabase.from("patients").select("id,user_id").in("id", refs),
      supabase.from("patients").select("id,user_id").in("user_id", refs),
    ]);

  if (byPatientIdError && !isRecoverableReadDbError(byPatientIdError)) {
    throw byPatientIdError;
  }
  if (byUserIdError && !isRecoverableReadDbError(byUserIdError)) {
    throw byUserIdError;
  }

  const rows = [
    ...(Array.isArray(byPatientId) ? byPatientId : []),
    ...(Array.isArray(byUserId) ? byUserId : []),
  ];
  const byId = new Map();
  const byUser = new Map();

  rows.forEach((row) => {
    const id = String(row?.id || "").trim();
    const userId = String(row?.user_id || "").trim();
    if (id && userId && !byId.has(id)) byId.set(id, userId);
    if (userId && !byUser.has(userId)) byUser.set(userId, userId);
  });

  for (const ref of refs) {
    if (byUser.has(ref)) return byUser.get(ref) || null;
    if (byId.has(ref)) return byId.get(ref) || null;
  }

  return directUserId || null;
}

async function resolveDoctorAuthUserId({ appointmentId, doctorUserId, doctorRef }) {
  const directUserId = String(doctorUserId || "").trim();
  const refs = Array.from(
    new Set(
      [doctorRef, directUserId]
        .map((value) => String(value || "").trim())
        .filter(Boolean)
    )
  );

  if (appointmentId) {
    const { data: appointmentRow, error: appointmentError } = await supabase
      .from("appointments")
      .select("doctor_id")
      .eq("id", String(appointmentId).trim())
      .maybeSingle();

    if (appointmentError && !isRecoverableReadDbError(appointmentError)) {
      throw appointmentError;
    }

    const appointmentDoctorId = String(appointmentRow?.doctor_id || "").trim();
    if (appointmentDoctorId && !refs.includes(appointmentDoctorId)) {
      refs.push(appointmentDoctorId);
    }
  }

  if (refs.length === 0) return directUserId || null;

  const [{ data: byProfileId, error: byProfileIdError }, { data: byUserId, error: byUserIdError }] =
    await Promise.all([
      supabase.from("doctor_profiles").select("id,user_id").in("id", refs),
      supabase.from("doctor_profiles").select("id,user_id").in("user_id", refs),
    ]);

  if (byProfileIdError && !isRecoverableReadDbError(byProfileIdError)) {
    throw byProfileIdError;
  }
  if (byUserIdError && !isRecoverableReadDbError(byUserIdError)) {
    throw byUserIdError;
  }

  const rows = [
    ...(Array.isArray(byProfileId) ? byProfileId : []),
    ...(Array.isArray(byUserId) ? byUserId : []),
  ];
  const byId = new Map();
  const byUser = new Map();

  rows.forEach((row) => {
    const id = String(row?.id || "").trim();
    const userId = String(row?.user_id || "").trim();
    if (id && userId && !byId.has(id)) byId.set(id, userId);
    if (userId && !byUser.has(userId)) byUser.set(userId, userId);
  });

  for (const ref of refs) {
    if (byUser.has(ref)) return byUser.get(ref) || null;
    if (byId.has(ref)) return byId.get(ref) || null;
  }

  return directUserId || null;
}


async function ehrRpc(name,args){const {data,error}=await supabase.rpc(name,args);if(error)throw error;return data;}
export async function requestAppointmentEhrAccess({appointmentId}) {
 const requestId=await ehrRpc('afrimed_request_appointment_ehr_access',{p_appointment_id:appointmentId});
 if(!requestId)throw Error('EHR request unavailable');
 return {requestId,requestedAt:new Date().toISOString(),status:'requested'};
}
export async function loadAppointmentEhrAccessState({appointmentId}) {
 const request=await ehrRpc('afrimed_get_appointment_ehr_access_state',{p_appointment_id:appointmentId});
 return request?{request,permission:null,status:request.status}:null;
}
async function decide({requestId,appointmentId},grant){
 const id=requestId||(await loadAppointmentEhrAccessState({appointmentId}))?.request?.id;
 if(!id)throw Error('A current Patient EHR request is required');
 await ehrRpc('afrimed_decide_appointment_ehr_access',{p_request_id:id,p_grant:grant});
 return {requestId:id,grantedAt:grant?new Date().toISOString():null,status:grant?'granted':'declined'};
}
export const grantAppointmentEhrAccess=args=>decide(args,true);
export const declineAppointmentEhrAccess=args=>decide(args,false);
export const loadPatientAppointmentEhrAccessState=loadAppointmentEhrAccessState;
export async function createAppointmentNotification(args){return insertNotification(args);}
export async function createUserNotification(args){return insertNotification(args);}
export async function revokeAppointmentEhrAccess({appointmentId}) {
 const {data:{user}}=await supabase.auth.getUser();
 const state=await loadAppointmentEhrAccessState({appointmentId});
 if(!state?.request)return {status:'revoked',hadPermission:false};
 if(state.request.patient_id===user?.id)await ehrRpc('afrimed_revoke_patient_ehr_access',{p_request_id:state.request.id});
 else {await ehrRpc('afrimed_enter_appointment_ehr',{p_appointment_id:appointmentId});await ehrRpc('afrimed_consume_appointment_ehr_access',{p_appointment_id:appointmentId});}
 return {revokedAt:new Date().toISOString(),status:'revoked',hadPermission:true};
}
export {resolveDoctorAuthUserId,resolvePatientAuthUserId};
